import { type ReactNode, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * A side sheet on the native <dialog> (modal: focus is trapped, Escape and the
 * backdrop close it, the page behind is inert) — the tally's contingent
 * drawer. Full width on a phone, a 36rem panel from the right otherwise.
 * `open` is the caller's (the URL's `?noc=`), so Back closes it too.
 */
export function Sheet({ open, title, subtitle, onClose, children }: { open: boolean; title: ReactNode; subtitle?: ReactNode; onClose: () => void; children: ReactNode }) {
	const { t } = useTranslation();
	const ref = useRef<HTMLDialogElement>(null);
	const titleId = useId();

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		if (open && !el.open) el.showModal();
		else if (!open && el.open) el.close();
	}, [open]);

	return (
		<dialog
			ref={ref}
			aria-labelledby={titleId}
			onClose={onClose}
			onCancel={(e) => {
				e.preventDefault();
				onClose();
			}}
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
			className="fixed inset-y-0 right-0 left-auto m-0 h-dvh max-h-dvh w-full max-w-[36rem] overflow-hidden border-rule border-l bg-sheet p-0 text-ink backdrop:bg-ink/45 open:flex open:flex-col print:hidden"
		>
			<header className="flex items-start justify-between gap-[0.75rem] border-rule border-b px-[1.25rem] py-[1rem]">
				<div className="flex min-w-0 flex-col gap-[0.25rem]">
					<h2 id={titleId} className="font-black text-[1.25rem] leading-tight">
						{title}
					</h2>
					{subtitle && <div className="text-[0.8125rem] text-muted">{subtitle}</div>}
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label={t("actions.close")}
					className="flex size-touch shrink-0 items-center justify-center rounded-btn text-[1.25rem] text-muted hover:bg-paper hover:text-ink"
				>
					×
				</button>
			</header>
			<div className="min-h-0 flex-1 overflow-y-auto px-[1.25rem] py-[1rem]">{open && children}</div>
		</dialog>
	);
}
